import type { DropDownListChangeEvent } from "@tql/ui-components-react/kendo/dropdowns";
import type { GridCustomCellProps } from "@tql/ui-components-react/kendo/grid";
import { DropDownList } from "@tql/ui-components-react/tql/dropdowns";

import type { EditDescriptor } from "~/features/commodities/hooks/use-commodity-editing";
import type { HandlingUnit, Unit } from "~/shared/types/quote-details-types";

type DropDownCellProps<T> = GridCustomCellProps & {
	edit: EditDescriptor;
	data: T[];
	nestedPropertyName: string | null;
	textField: string;
	valueField: string;
	dataItemKey: string;
};

// Extended type for searchable data items
type SearchableHandlingUnit = HandlingUnit & {
	originalFreightClass?: Unit;
	originalUnitType?: Unit;
};

export const DropDownCell = <T,>({
	edit,
	data,
	nestedPropertyName,
	textField,
	valueField,
	dataItemKey,
	...gridProps
}: DropDownCellProps<T>) => {
	const dataItem = gridProps.dataItem as SearchableHandlingUnit | undefined;

	// Guard clause: if no dataItem, render empty cell
	if (!dataItem) {
		return <td {...gridProps.tdProps} />;
	}

	const field = gridProps.field || "";
	// Use original value if available (for searchable data), otherwise use current value
	const originalField =
		`original${field.charAt(0).toUpperCase() + field.slice(1)}` as keyof SearchableHandlingUnit;
	const dataValue =
		dataItem[originalField] ?? (dataItem[field as keyof HandlingUnit] as Unit);

	const handleChange = (e: DropDownListChangeEvent) => {
		if (gridProps.onChange) {
			gridProps.onChange({
				dataIndex: 0,
				dataItem: gridProps.dataItem as HandlingUnit,
				field: gridProps.field,
				syntheticEvent: e.syntheticEvent,
				value: e.target.value as T,
			});
		}
	};

	const isInEdit = edit[dataItem.id] === true;

	// Find the current value object from the dropdown data
	const currentValue = data.find(
		(item) =>
			String(item[valueField as keyof T]) ===
			String((dataValue as Unit)?.[valueField as keyof Unit]),
	);

	// Display value for non-edit mode
	const displayValue =
		dataValue && nestedPropertyName
			? String((dataValue as Unit)[nestedPropertyName as keyof Unit] ?? "")
			: "";

	return (
		<td {...gridProps.tdProps}>
			{isInEdit ? (
				<DropDownList
					data={data}
					dataItemKey={dataItemKey}
					onChange={handleChange}
					textField={textField}
					value={currentValue}
				/>
			) : (
				displayValue
			)}
		</td>
	);
};
